import { Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { environment } from 'src/environments/environment';

/** ota/manifest.json publicado pelo deploy web (deploy-homologacao.yml / deploy-producao.yml). */
interface ManifestoAtualizacao {
    versao: string;
    /** Caminho do .zip relativo ao manifesto. */
    arquivo: string;
    /** SHA-256 do .zip. */
    checksum: string;
    /** NATIVE_VERSION do commit que gerou o pacote. */
    versaoNativaMinima: number;
    commit: string;
}

/**
 * Atualização pelo ar dos apps (Capgo, hospedado por nós). O deploy web publica o mesmo pacote do portal
 * em <urlPortal>/ota/: homologação para os apps do teste interno/TestFlight e produção para os publicados
 * (o environment de cada build define o urlPortal). O pacote novo é baixado em segundo plano e entra na
 * próxima abertura; se não confirmar que abriu (notifyAppReady), o plugin volta para o anterior.
 */
@Injectable({
    providedIn: 'root',
})
export class AtualizacaoService {
    async verificar(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        // Esta versão abriu: sem a confirmação, o plugin volta sozinho para a anterior
        await CapacitorUpdater.notifyAppReady();

        const urlPortal = (environment as { urlPortal?: string }).urlPortal;
        if (!urlPortal) {
            return;
        }
        const base = `${urlPortal}/ota/`;
        try {
            // HTTP nativo: o fetch do WebView (origem localhost) é barrado por CORS, que o CloudFront não libera
            const resposta = await CapacitorHttp.get({
                url: `${base}manifest.json`,
                headers: { 'Cache-Control': 'no-cache' },
                responseType: 'json',
            });
            if (resposta.status !== 200) {
                console.warn(`[atualizacao] manifesto indisponível (HTTP ${resposta.status})`);
                return;
            }
            // Sem o arquivo, o CloudFront devolve o index.html (texto, não o manifesto)
            const manifesto = resposta.data as ManifestoAtualizacao;
            if (typeof manifesto?.versao !== 'string') {
                console.warn('[atualizacao] resposta sem manifesto válido');
                return;
            }

            const nativa = Number((await CapacitorUpdater.getBuiltinVersion()).version) || 0;
            if (manifesto.versaoNativaMinima > nativa) {
                console.info(
                    `[atualizacao] ${manifesto.versao} exige versão nativa ${manifesto.versaoNativaMinima} (app tem ${nativa}): atualize pela loja`,
                );
                return;
            }
            const atual = await CapacitorUpdater.current();
            const proxima = await CapacitorUpdater.getNextBundle();
            if (
                atual.bundle.version === manifesto.versao ||
                proxima?.version === manifesto.versao
            ) {
                return;
            }
            const pacote = await CapacitorUpdater.download({
                url: new URL(manifesto.arquivo, base).href,
                version: manifesto.versao,
                checksum: manifesto.checksum,
            });
            await CapacitorUpdater.next({ id: pacote.id });
            console.info(
                `[atualizacao] ${manifesto.versao} (commit ${manifesto.commit}) baixada; entra na próxima abertura`,
            );
        } catch (erro) {
            console.warn('[atualizacao] não foi possível verificar a atualização', erro);
        }
    }
}
